import React, { useState, useMemo } from 'react';
import { TeamCard } from '../components/TeamCard';
import { TacticalPitch } from '../components/TacticalPitch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FORMATIONS, COMPETITIONS, getPlayerStat, getTeamOverall } from '../utils/gameData';
import { buildChampionsLeagueTeams, buildCWCTeams, buildEuropaLeagueTeams, buildConferenceLeagueTeams } from '../utils/realTeams';
import { buildWorldCupTeams, buildAllNationalTeams } from '../utils/worldCup';
import { makeSubstitution } from '../utils/simulationEngine';
import { SubstitutionModal } from '../components/SubstitutionModal';
import { TeamLogo } from '../components/TeamLogo';
import { FlagBackdrop } from '../components/NationFlag';
import { MagnifyingGlass, SortAscending, PencilSimple, Users, Globe, Flag, GlobeHemisphereWest, Plus, ArrowsLeftRight, Shield } from '@phosphor-icons/react';

const POSITIONS_LIST = ['GK','CB','LB','RB','LWB','RWB','SW','CDM','CM','CAM','LM','RM','LW','RW','CF','ST','SS','LF','RF'];

const getPositionColor = (position) => {
  if (position === 'GK') return 'position-gk';
  if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(position)) return 'position-def';
  if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(position)) return 'position-mid';
  return 'position-att';
};
const getPotentialClass = (potential) => {
  if (potential >= 90) return 'potential-elite';
  if (potential >= 80) return 'potential-high';
  if (potential >= 70) return 'potential-mid';
  return 'potential-low';
};

const PlayerRow = ({ player, isBench = false, onEdit }) => {
  if (!player) return null;
  return (
    <div data-testid={`${isBench ? 'bench' : 'roster'}-player-${player.number}`}
      className={`flex items-center gap-2 px-2 py-2 hover:bg-[#1A1A1F] rounded-sm group ${isBench ? 'opacity-70' : ''}`}>
      <span className="font-mono font-bold text-white w-6 text-sm">{player.number}</span>
      <span className={`position-badge ${getPositionColor(player.position)}`}>{player.position}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">
          {player.name}
          {player.injuredWeeks > 0 && <span className="text-[#FF6B00] ml-1 text-xs">({player.injuredWeeks}w)</span>}
        </p>
        <div className="flex items-center gap-2 text-[10px] text-[#6E6E73]">
          <span className="nationality-flag">{player.flag}</span>
          <span>{player.nationality}</span>
          <span>Age {player.age}</span>
        </div>
      </div>
      <div className="text-right flex-shrink-0 w-20">
        <div className="flex items-center gap-2 justify-end">
          <span className="font-mono text-sm text-white">{player.overall}</span>
          <span className="text-[10px] text-[#6E6E73]">/</span>
          <span className={`font-mono text-sm ${player.potential >= 85 ? 'text-[#007AFF]' : player.potential >= 75 ? 'text-[#32D74B]' : 'text-[#A0A0A5]'}`}>
            {player.potential}
          </span>
        </div>
        <div className="potential-bar w-full mt-0.5">
          <div className={`potential-bar-fill ${getPotentialClass(player.potential)}`} style={{ width: `${(player.potential / 99) * 100}%` }} />
        </div>
      </div>
      <button onClick={() => onEdit?.(player)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#6E6E73] hover:text-white ml-1" data-testid={`edit-player-${player.number}`}>
        <PencilSimple size={14} />
      </button>
    </div>
  );
};

export const TeamsPage = ({
  teams = [],
  championsLeagueTeams = [],
  europaLeagueTeams = [],
  conferenceLeagueTeams = [],
  cwcTeams = [],
  wcTeams = [],
  onUpdateTeam, onUpdateTeams,
  onUpdateChampionsLeagueTeams, onUpdateEuropaLeagueTeams, onUpdateConferenceLeagueTeams,
  onUpdateCwcTeams, onUpdateWcTeams
}) => {
  const [pool, setPool] = useState('ewfl'); // ewfl | championsLeague | cwc | wc
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [edit, setEdit] = useState({ name: '', position: 'CM', overall: 70, potential: 80, age: 20, nationality: 'Japan' });
  const [statComp, setStatComp] = useState('total');
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ name: '', position: 'CM', overall: 70, potential: 80, age: 20, nationality: 'Japan', flag: "🇯🇵" });

  // Nations is the only pool kept fully local (not persisted to gameState).
  const [nationsLocal, setNationsLocal] = useState([]);

  const activeTeams =
    pool === 'ewfl' ? teams :
    pool === 'championsLeague' ? championsLeagueTeams :
    pool === 'europaLeague' ? europaLeagueTeams :
    pool === 'conferenceLeague' ? conferenceLeagueTeams :
    pool === 'cwc' ? cwcTeams :
    pool === 'wc' ? wcTeams :
    pool === 'nations' ? nationsLocal : [];

  // Lazy init: build pools the first time the user opens them.
  const ensurePoolInitialized = (newPool) => {
    if (newPool === 'championsLeague' && (!championsLeagueTeams || championsLeagueTeams.length === 0)) {
      onUpdateChampionsLeagueTeams?.(buildChampionsLeagueTeams());
    }
    if (newPool === 'europaLeague' && (!europaLeagueTeams || europaLeagueTeams.length === 0)) {
      onUpdateEuropaLeagueTeams?.(buildEuropaLeagueTeams());
    }
    if (newPool === 'conferenceLeague' && (!conferenceLeagueTeams || conferenceLeagueTeams.length === 0)) {
      onUpdateConferenceLeagueTeams?.(buildConferenceLeagueTeams());
    }
    if (newPool === 'cwc' && (!cwcTeams || cwcTeams.length === 0)) {
      onUpdateCwcTeams?.(buildCWCTeams());
    }
    if (newPool === 'wc' && (!wcTeams || wcTeams.length === 0)) {
      onUpdateWcTeams?.(buildWorldCupTeams(teams));
    }
    if (newPool === 'nations' && nationsLocal.length === 0) {
      // Make sure WC teams exist first, then build Nations REUSING those exact
      // WC team objects so the WC tab and Nations tab show identical rosters.
      let wcReady = wcTeams && wcTeams.length > 0 ? wcTeams : null;
      if (!wcReady) {
        wcReady = buildWorldCupTeams(teams);
        onUpdateWcTeams?.(wcReady);
      }
      setNationsLocal(buildAllNationalTeams(teams, wcReady));
    }
  };
  const switchPool = (p) => {
    ensurePoolInitialized(p);
    setPool(p);
    setSelectedTeam(null);
  };

  const updateTeamInPool = (updatedTeam) => {
    const updated = activeTeams.map(t => t.id === updatedTeam.id ? updatedTeam : t);
    if (pool === 'ewfl') onUpdateTeams?.(updated);
    else if (pool === 'championsLeague') onUpdateChampionsLeagueTeams?.(updated);
    else if (pool === 'europaLeague') onUpdateEuropaLeagueTeams?.(updated);
    else if (pool === 'conferenceLeague') onUpdateConferenceLeagueTeams?.(updated);
    else if (pool === 'cwc') onUpdateCwcTeams?.(updated);
    else if (pool === 'wc') onUpdateWcTeams?.(updated);
    else if (pool === 'nations') setNationsLocal(updated);
    setSelectedTeam(updatedTeam);
  };

  const handleLineupSwap = (outNum, inNum) => {
    if (!selectedTeam) return;
    const updated = makeSubstitution(selectedTeam, outNum, inNum);
    updateTeamInPool(updated);
  };

  const handleFormationChange = (newFormation) => {
    if (!selectedTeam) return;
    updateTeamInPool({ ...selectedTeam, formation: newFormation });
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setEdit({
      name: player.name || '',
      position: player.position || 'CM',
      overall: player.overall || 70,
      potential: player.potential || 80,
      age: player.age || 20,
      nationality: player.nationality || 'Japan'
    });
  };
  const savePlayerEdit = () => {
    if (!editingPlayer || !selectedTeam) return;
    const updatedTeam = {
      ...selectedTeam,
      players: selectedTeam.players.map(p => p.id === editingPlayer.id
        ? { ...p, name: edit.name, position: edit.position, overall: Number(edit.overall), potential: Number(edit.potential), age: Number(edit.age), nationality: edit.nationality }
        : p)
    };
    updateTeamInPool(updatedTeam);
    setEditingPlayer(null);
  };

  const addPlayerToTeam = () => {
    if (!selectedTeam || !newPlayer.name.trim()) return;
    const nextNumber = selectedTeam.players.length > 0 ? Math.max(...selectedTeam.players.map(p => p.number)) + 1 : 1;
    const player = {
      id: `${selectedTeam.id}-custom-${Date.now()}`,
      number: nextNumber,
      name: newPlayer.name.trim(),
      position: newPlayer.position,
      overall: Number(newPlayer.overall),
      potential: Number(newPlayer.potential),
      age: Number(newPlayer.age),
      nationality: newPlayer.nationality,
      flag: newPlayer.flag,
      seasonStartOverall: Number(newPlayer.overall),
      stats: { league: { goals: 0, assists: 0, yellowCards: 0, redCards: 0, matchesPlayed: 0, cleanSheets: 0 }, cup: { goals: 0, assists: 0, yellowCards: 0, redCards: 0, matchesPlayed: 0, cleanSheets: 0 }, championsLeague: { goals: 0, assists: 0, yellowCards: 0, redCards: 0, matchesPlayed: 0, cleanSheets: 0 }, cwc: { goals: 0, assists: 0, yellowCards: 0, redCards: 0, matchesPlayed: 0, cleanSheets: 0 } },
      isRookie: Number(newPlayer.age) <= 19
    };
    const updatedTeam = {
      ...selectedTeam,
      players: [...selectedTeam.players, player],
      bench: [...selectedTeam.bench, nextNumber]
    };
    updateTeamInPool(updatedTeam);
    setShowAddPlayer(false);
    setNewPlayer({ name: '', position: 'CM', overall: 70, potential: 80, age: 20, nationality: 'Japan', flag: "🇯🇵" });
  };

  const filteredTeams = useMemo(() => [...activeTeams]
    .filter(team => (team.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) || (team.city?.toLowerCase() || '').includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'points': return (b.stats?.points || 0) - (a.stats?.points || 0);
        case 'overall': {
          const oa = getTeamOverall(a) || 0;
          const ob = getTeamOverall(b) || 0;
          return ob - oa;
        }
        default: return (a.name || '').localeCompare(b.name || '');
      }
    }), [activeTeams, searchQuery, sortBy]);

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">Teams</h1>
            <p className="text-[#A0A0A5] mt-1">Edit any player's stats, name, position, age, and nationality</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 bg-[#0A0D1F] border border-[#1E2240] rounded-md p-1">
            <button data-testid="pool-ewfl" onClick={() => switchPool('ewfl')} className={`px-2 py-1.5 text-xs font-heading uppercase rounded-sm flex items-center gap-1 transition-all ${pool === 'ewfl' ? 'bg-[#00E5FF] text-black font-semibold shadow-[0_0_12px_rgba(0,229,255,0.4)]' : 'text-[#7A86B5] hover:text-white'}`}>
              <Users size={12} /> JWFL
            </button>
            <button data-testid="pool-ucl" onClick={() => switchPool('championsLeague')} className={`px-2 py-1.5 text-xs font-heading uppercase rounded-sm flex items-center gap-1 transition-all ${pool === 'championsLeague' ? 'bg-[#CCFF00] text-black font-semibold shadow-[0_0_12px_rgba(204,255,0,0.4)]' : 'text-[#7A86B5] hover:text-white'}`}>
              <Globe size={12} /> ASIAN
            </button>
            <button data-testid="pool-cwc" onClick={() => switchPool('cwc')} className={`px-2 py-1.5 text-xs font-heading uppercase rounded-sm flex items-center gap-1 transition-all ${pool === 'cwc' ? 'bg-[#FFB400] text-black font-semibold shadow-[0_0_12px_rgba(255,180,0,0.4)]' : 'text-[#7A86B5] hover:text-white'}`}>
              <Flag size={12} /> CWC
            </button>
            <button data-testid="pool-wc" onClick={() => switchPool('wc')} className={`px-2 py-1.5 text-xs font-heading uppercase rounded-sm flex items-center gap-1 transition-all ${pool === 'wc' ? 'bg-[#FF4D00] text-white font-semibold shadow-[0_0_12px_rgba(255,77,0,0.4)]' : 'text-[#7A86B5] hover:text-white'}`}>
              <GlobeHemisphereWest size={12} /> WC
            </button>
            <button data-testid="pool-nations" onClick={() => switchPool('nations')} className={`px-2 py-1.5 text-xs font-heading uppercase rounded-sm flex items-center gap-1 transition-all ${pool === 'nations' ? 'bg-[#FF006E] text-white font-semibold shadow-[0_0_12px_rgba(255,0,110,0.4)]' : 'text-[#7A86B5] hover:text-white'}`}>
              <GlobeHemisphereWest size={12} /> Nations
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="card-surface rounded-sm">
              <div className="p-4 border-b border-[#2C2C35] space-y-3">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
                  <Input data-testid="team-search-input" placeholder="Search teams..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-[#0A0A0C] border-[#2C2C35] text-white placeholder:text-[#6E6E73]" />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="sort-select">
                    <SortAscending size={16} className="mr-2" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121216] border-[#2C2C35]">
                    <SelectItem value="name">Sort by Name</SelectItem>
                    <SelectItem value="points">Sort by Points</SelectItem>
                    <SelectItem value="overall">Sort by Overall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ScrollArea className="h-[600px]">
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
                  {filteredTeams.length === 0 ? (
                    <p className="text-[#6E6E73] text-sm text-center p-4">
                      {pool === 'ewfl' ? 'No JWFL teams. Start a new game.' : pool === 'championsLeague' ? 'Click an Asian Cup team to initialize.' : 'Click a CWC team to initialize.'}
                    </p>
                  ) : filteredTeams.map(team => (
                    <TeamCard key={team.id} team={team} onClick={setSelectedTeam} isSelected={selectedTeam?.id === team.id} />
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="lg:col-span-2">
            {selectedTeam ? (
              <div className="space-y-6">
                <div className="card-surface rounded-sm p-6 relative overflow-hidden">
                  <FlagBackdrop country={selectedTeam.country} opacity={0.16} />
                  <div className="relative z-10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-sm bg-[#0A0A0C] border border-[#2C2C35] p-1">
                        <TeamLogo team={selectedTeam} size={72} />
                      </div>
                      <div>
                        <h2 className="font-heading font-black text-2xl sm:text-3xl text-white uppercase tracking-wide">{selectedTeam.name}</h2>
                        <p className="text-[#A0A0A5] mt-1">{selectedTeam.city || selectedTeam.country}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Select value={selectedTeam.formation || '4-3-3'} onValueChange={handleFormationChange}>
                        <SelectTrigger className="w-[130px] bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="formation-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121216] border-[#2C2C35]">
                          {FORMATIONS.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setShowSubModal(true)} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white font-bold" data-testid="open-sub-btn" disabled={!selectedTeam?.bench?.length}>
                          <ArrowsLeftRight size={14} className="mr-1" /> Sub
                        </Button>
                        <Button size="sm" onClick={() => setShowAddPlayer(true)} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" data-testid="open-add-player-btn">
                          <Plus size={14} className="mr-1" /> Add Player
                        </Button>
                      </div>
                    </div>
                  </div>
                  {pool === 'ewfl' && (
                    <div className="grid grid-cols-5 gap-4 mt-6 pt-6 border-t border-[#2C2C35]">
                      <StatBox label="Played" value={selectedTeam.stats?.played || 0} color="text-white" />
                      <StatBox label="Won" value={selectedTeam.stats?.won || 0} color="text-[#32D74B]" />
                      <StatBox label="Drawn" value={selectedTeam.stats?.drawn || 0} color="text-[#A0A0A5]" />
                      <StatBox label="Lost" value={selectedTeam.stats?.lost || 0} color="text-[#FF3B30]" />
                      <StatBox label="Points" value={selectedTeam.stats?.points || 0} color="text-[#007AFF]" />
                    </div>
                  )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><TacticalPitch team={selectedTeam} /></div>
                  <div className="card-surface rounded-sm">
                    <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center justify-between">
                      <h3 className="font-heading font-bold text-white uppercase tracking-wide">Squad ({selectedTeam.players.length})</h3>
                      <span className="text-[10px] text-[#6E6E73] uppercase tracking-wider">OVR / POT</span>
                    </div>
                    <ScrollArea className="h-[400px]">
                      <div className="p-1">
                        <p className="text-xs font-bold uppercase text-[#32D74B] px-2 py-1 tracking-wider">Starting XI</p>
                        {selectedTeam.lineup.map(num => {
                          const p = selectedTeam.players.find(pl => pl.number === num);
                          return <PlayerRow key={num} player={p} onEdit={openEditDialog} />;
                        })}
                        <p className="text-xs font-bold uppercase text-[#A0A0A5] px-2 py-1 mt-3 tracking-wider">Substitutes ({selectedTeam.bench.length})</p>
                        {selectedTeam.bench.map(num => {
                          const p = selectedTeam.players.find(pl => pl.number === num);
                          return <PlayerRow key={num} player={p} isBench onEdit={openEditDialog} />;
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                </div>

                {/* Player Stats by Competition */}
                <div className="rounded-xl relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(180deg, #0A0D1F 0%, #070A1A 100%)',
                    border: '1px solid #1E2240',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.35)',
                  }}>
                  <FlagBackdrop country={selectedTeam.country} opacity={0.08} />
                  <div className="relative z-10">
                  <div className="border-b border-[#1E2240] px-5 py-4">
                    <h3 className="font-heading font-semibold text-white uppercase text-sm tracking-[0.22em]">Player Stats · By Competition</h3>
                  </div>
                  <Tabs value={statComp} onValueChange={setStatComp} className="w-full">
                    <TabsList className="bg-[#070A1A] border-b border-[#1E2240] w-full flex flex-wrap gap-0 rounded-none p-0 h-auto">
                      <TabsTrigger value="total" className="data-[state=active]:bg-[#101433] data-[state=active]:text-[#00E5FF] data-[state=active]:shadow-[inset_0_-2px_0_#00E5FF] text-[11px] uppercase tracking-[0.14em] flex-1 min-w-[80px] rounded-none py-3 transition-all" data-testid="team-stat-total">Total</TabsTrigger>
                      {COMPETITIONS.map(c => (
                        <TabsTrigger key={c.key} value={c.key} className="data-[state=active]:bg-[#101433] data-[state=active]:text-[#00E5FF] data-[state=active]:shadow-[inset_0_-2px_0_#00E5FF] text-[11px] uppercase tracking-[0.14em] flex-1 min-w-[80px] rounded-none py-3 transition-all" data-testid={`team-stat-${c.key}`}>{c.label}</TabsTrigger>
                      ))}
                    </TabsList>
                    {['total', ...COMPETITIONS.map(c => c.key)].map(compKey => (
                      <TabsContent key={compKey} value={compKey}>
                        <PlayerStatsTable players={selectedTeam.players} comp={compKey} />
                      </TabsContent>
                    ))}
                  </Tabs>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl h-[500px] flex flex-col items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(180deg, #0A0D1F 0%, #070A1A 100%)',
                  border: '1px solid #1E2240',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.35)',
                }}>
                <div className="w-14 h-14 rounded-full border border-[#1E2240] flex items-center justify-center"
                  style={{ background: 'rgba(0,229,255,0.06)' }}>
                  <Shield size={22} className="text-[#00E5FF] opacity-70" weight="duotone" />
                </div>
                <p className="text-[#7A86B5] text-sm tracking-wide">Select a team to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <SubstitutionModal
        isOpen={showSubModal}
        onClose={() => setShowSubModal(false)}
        team={selectedTeam}
        onSubstitute={handleLineupSwap}
        subsRemaining={'∞'}
        mode="lineup"
      />

      <Dialog open={!!editingPlayer} onOpenChange={() => setEditingPlayer(null)}>        <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-md" data-testid="edit-player-dialog">
          <DialogHeader><DialogTitle className="font-heading text-white uppercase">Edit Player</DialogTitle></DialogHeader>
          {editingPlayer && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-lg font-bold text-white">{editingPlayer.flag} {edit.name}</p>
                <p className="text-xs text-[#6E6E73]">Editing {editingPlayer.name}</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Name</label>
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="edit-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Position</label>
                  <Select value={edit.position} onValueChange={(v) => setEdit({ ...edit, position: v })}>
                    <SelectTrigger className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="edit-position-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121216] border-[#2C2C35] max-h-60">
                      {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Age</label>
                  <Input type="number" min="15" max="45" value={edit.age} onChange={(e) => setEdit({ ...edit, age: e.target.value })} className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="edit-age-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Nationality</label>
                <Input value={edit.nationality} onChange={(e) => setEdit({ ...edit, nationality: e.target.value })} className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="edit-nationality-input" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs font-bold uppercase text-[#A0A0A5]">Overall</label>
                  <span className="font-mono text-sm text-white">{edit.overall}</span>
                </div>
                <Slider value={[Number(edit.overall)]} onValueChange={(v) => setEdit({ ...edit, overall: v[0] })} min={40} max={99} step={1} data-testid="edit-ovr-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-xs font-bold uppercase text-[#A0A0A5]">Potential</label>
                  <span className="font-mono text-sm text-[#007AFF]">{edit.potential}</span>
                </div>
                <Slider value={[Number(edit.potential)]} onValueChange={(v) => setEdit({ ...edit, potential: v[0] })} min={40} max={99} step={1} data-testid="edit-pot-slider" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPlayer(null)} className="bg-transparent border-[#2C2C35] text-white hover:bg-white hover:text-black">Cancel</Button>
            <Button onClick={savePlayerEdit} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" data-testid="save-player-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showAddPlayer} onOpenChange={setShowAddPlayer}>
        <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-md" data-testid="add-player-dialog">
          <DialogHeader><DialogTitle className="font-heading text-white uppercase">Add New Player</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#A0A0A5]">Adding to <span className="text-white font-bold">{selectedTeam?.name}</span></p>
            <div>
              <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Name</label>
              <Input value={newPlayer.name} onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })} placeholder="Player name" className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="new-player-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Position</label>
                <Select value={newPlayer.position} onValueChange={(v) => setNewPlayer({ ...newPlayer, position: v })}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="new-player-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121216] border-[#2C2C35] max-h-60">
                    {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Age</label>
                <Input type="number" min="15" max="45" value={newPlayer.age} onChange={(e) => setNewPlayer({ ...newPlayer, age: e.target.value })} className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="new-player-age-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Nationality</label>
                <Input value={newPlayer.nationality} onChange={(e) => setNewPlayer({ ...newPlayer, nationality: e.target.value })} placeholder="Japan" className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="new-player-nationality-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 block">Flag Emoji</label>
                <Input value={newPlayer.flag} onChange={(e) => setNewPlayer({ ...newPlayer, flag: e.target.value })} placeholder="🇯🇵" className="bg-[#0A0A0C] border-[#2C2C35] text-white" data-testid="new-player-flag-input" />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#A0A0A5]">Overall</label>
                <span className="font-mono text-sm text-white">{newPlayer.overall}</span>
              </div>
              <Slider value={[Number(newPlayer.overall)]} onValueChange={(v) => setNewPlayer({ ...newPlayer, overall: v[0] })} min={40} max={99} step={1} data-testid="new-player-ovr-slider" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#A0A0A5]">Potential</label>
                <span className="font-mono text-sm text-[#007AFF]">{newPlayer.potential}</span>
              </div>
              <Slider value={[Number(newPlayer.potential)]} onValueChange={(v) => setNewPlayer({ ...newPlayer, potential: v[0] })} min={40} max={99} step={1} data-testid="new-player-pot-slider" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlayer(false)} className="bg-transparent border-[#2C2C35] text-white hover:bg-white hover:text-black">Cancel</Button>
            <Button onClick={addPlayerToTeam} disabled={!newPlayer.name.trim()} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" data-testid="confirm-add-player-btn">
              <Plus size={14} className="mr-1" /> Add to Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatBox = ({ label, value, color }) => (
  <div className="text-center">
    <p className={`font-mono text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-[#6E6E73] uppercase">{label}</p>
  </div>
);

const PlayerStatsTable = ({ players, comp }) => {
  const rows = [...players].map(p => ({
    ...p,
    goals: getPlayerStat(p, 'goals', comp),
    assists: getPlayerStat(p, 'assists', comp),
    mp: getPlayerStat(p, 'matchesPlayed', comp),
    cs: getPlayerStat(p, 'cleanSheets', comp),
    yc: getPlayerStat(p, 'yellowCards', comp)
  })).filter(p => p.goals + p.assists + p.cs > 0)
    .sort((a, b) => (b.goals + b.assists) - (a.goals + a.assists));
  if (rows.length === 0) return <p className="text-[#6E6E73] text-sm p-4 text-center">No stats in this competition yet.</p>;
  return (
    <ScrollArea className="h-[300px]">
      <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
        {rows.map(p => (
          <div key={p.id} className="flex items-center gap-2 p-2 bg-[#0A0A0C] rounded-sm">
            <span className="text-[10px] font-mono text-[#6E6E73] w-5">#{p.number}</span>
            <span className="nationality-flag text-sm">{p.flag}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{p.name}</p>
              <p className="text-[10px] text-[#6E6E73]">{p.position} • MP: {p.mp}</p>
            </div>
            <div className="flex gap-2 text-xs">
              <span className="text-[#FF3B30]" title="Goals">⚽ {p.goals}</span>
              <span className="text-[#007AFF]" title="Assists">🅰 {p.assists}</span>
              {p.position === 'GK' && <span className="text-[#32D74B]">🥅 {p.cs}</span>}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};
